/**
 * Buy-target band maths, shared by the editor and the collapsed row summary.
 *
 * A "band" is how the merchant states a target — "2021 & 2022 = max 210,000
 * HKD" — while storage is one row per year (see the core_list_price_targets
 * migration). These functions are the only place the two shapes meet, and they
 * live outside both components so each of those files exports a component and
 * nothing else (the react-refresh lint rule, and the repo's warning ceiling).
 */

/** Saved per-year rows → the bands they were entered as, grouped by price. */
export const toBands = (targets = []) => {
  const byPrice = new Map();

  targets.forEach((target) => {
    const key = `${target.max_price}|${target.currency}`;
    if (!byPrice.has(key)) {
      byPrice.set(key, {
        years: [],
        maxPrice: target.max_price,
        currency: target.currency,
      });
    }
    byPrice.get(key).years.push(target.year);
  });

  return Array.from(byPrice.values()).sort(
    (a, b) => Math.min(...a.years) - Math.min(...b.years)
  );
};

/** The same grouping, with years as strings — what ScopeSelect's values are. */
export const toEditableBands = (targets = []) =>
  toBands(targets).map((band) => ({
    ...band,
    years: band.years.map(String),
  }));

/** Bands → the flat per-year payload the API takes. */
export const toPayload = (bands) =>
  bands.flatMap((band) =>
    (band.years || []).map((year) => ({
      year: Number(year),
      max_price: Number(band.maxPrice),
      currency: band.currency,
    }))
  );

/**
 * Years claimed by more than one band. The unique index on
 * (core_list_item_id, year) means such a save is ambiguous, so it is refused
 * with the offending years named rather than letting one band silently
 * overwrite another.
 */
export const duplicateYears = (bands) => {
  const seen = new Set();
  const duplicates = new Set();

  bands.forEach((band) => {
    (band.years || []).forEach((year) => {
      if (seen.has(String(year))) duplicates.add(String(year));
      seen.add(String(year));
    });
  });

  return Array.from(duplicates).sort();
};

/**
 * What each band ACTUALLY covers, as a label.
 *
 * Matching is "year and above": a band applies to its own years and every later
 * year until the next band takes over, and the last band never closes. So the
 * years the merchant typed are the band's START, not its extent, and printing
 * them verbatim would under-report what is being watched — "2024-25" while 2026
 * and 2027 are matched against it too.
 *
 * Bands must be year-ascending (toBands sorts them).
 *
 *   [2021,2022] + [2024,2025]  →  "2021-23"  and  "2024+"
 */
export const bandCoverageLabels = (bands) => {
  const starts = bands.map((band) => Math.min(...band.years.map(Number)));

  return bands.map((band, index) => {
    const start = starts[index];
    const nextStart = starts[index + 1];

    // The last band stays open-ended — that is the whole point of the rule.
    if (nextStart === undefined) return `${start}+`;

    const end = nextStart - 1;
    if (end <= start) return String(start);

    const tail =
      Math.floor(start / 100) === Math.floor(end / 100)
        ? String(end).slice(-2)
        : String(end);
    return `${start}-${tail}`;
  });
};

/** Consecutive years collapse to a range: [2021,2022,2024] → "2021-22, 2024". */
export const formatYears = (years) => {
  const sorted = Array.from(new Set(years.map(Number))).sort((a, b) => a - b);
  const parts = [];
  let start = null;
  let previous = null;

  const flush = () => {
    if (start === null) return;
    if (start === previous) {
      parts.push(String(start));
      return;
    }
    // Short form within the same century ("2021-22"), full year across one so
    // "1998-2001" never reads as "1998-01".
    const tail =
      Math.floor(start / 100) === Math.floor(previous / 100)
        ? String(previous).slice(-2)
        : String(previous);
    parts.push(`${start}-${tail}`);
  };

  sorted.forEach((year) => {
    if (start === null) {
      start = year;
      previous = year;
      return;
    }
    if (year === previous + 1) {
      previous = year;
      return;
    }
    flush();
    start = year;
    previous = year;
  });
  flush();

  return parts.join(", ");
};
